import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Award, Package, MapPin, User as UserIcon } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

export default function Account() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) api.get("/orders").then((r) => setOrders(r.data || []));
  }, [user]);

  if (loading || !user) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/40 tracking-luxury uppercase text-sm">Loading…</div>;
  }

  return (
    <div data-testid="account-page" className="bg-poche-black text-white">
      <section className="container-x py-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Account</p>
            <h1 className="font-serif text-5xl md:text-6xl mt-3">Bonjour, {user.name.split(" ")[0]}.</h1>
            <p className="text-white/55 mt-3">{user.email} · {user.loyalty_tier} member · {user.loyalty_points} points</p>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="btn-outline-gold">
            <LogOut size={14} /> Sign Out
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-14">
          {/* sidebar widgets */}
          <div className="md:col-span-4 space-y-6">
            <Widget i={Award} t={`${user.loyalty_tier} Status`} v={`${user.loyalty_points} points`} />
            <Widget i={Package} t="Orders" v={`${orders.length} placed`} />
            <Widget i={MapPin} t="Fit Profile" v={user.fit_profile?.face_shape ? `${user.fit_profile.face_shape} · ${user.fit_profile.nose_bridge} bridge` : "Not set"} cta={<Link to="/app/fit-profile" className="text-poche-gold text-[11px] tracking-luxury uppercase">Set →</Link>} />
            <Widget i={UserIcon} t="Member Since" v={new Date(user.created_at).toLocaleDateString()} />
          </div>

          {/* orders */}
          <div className="md:col-span-8">
            <h2 className="font-serif text-3xl">Your Orders</h2>
            {orders.length === 0 ? (
              <p className="text-white/55 mt-4">No orders yet. <Link to="/shop" className="text-poche-gold">Enter the collection →</Link></p>
            ) : (
              <div className="mt-6 border border-white/10 divide-y divide-white/10">
                {orders.map((o) => (
                  <div key={o.order_id} className="p-6 flex flex-wrap items-center justify-between gap-4" data-testid={`order-row-${o.order_id}`}>
                    <div>
                      <div className="text-[11px] tracking-luxury uppercase text-white/55">#{o.order_id.toUpperCase()}</div>
                      <div className="font-serif text-xl mt-1">{o.items.length} pieces · ${o.total.toLocaleString()}</div>
                      <div className="text-xs text-white/45 mt-1">Tracking · {o.tracking_number}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] tracking-luxury uppercase px-3 py-1 border border-poche-gold/50 text-poche-gold">{o.fulfillment_status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Widget({ i: I, t, v, cta }) {
  return (
    <div className="border border-white/10 p-6 flex items-start gap-4">
      <I size={20} strokeWidth={1.4} className="text-poche-gold mt-1" />
      <div className="flex-1">
        <div className="text-[10px] tracking-luxury uppercase text-white/45">{t}</div>
        <div className="font-serif text-2xl mt-1">{v}</div>
        {cta && <div className="mt-2">{cta}</div>}
      </div>
    </div>
  );
}
